/**
 * 用户状态管理
 * 使用 @vue/reactivity 实现响应式状态
 */

import { ref, computed, type Ref, type ComputedRef } from '@vue/reactivity'
import type { IStorage } from '../storage/Storage'
import type { UserInfo, VipInfo, PointsInfo, Task, UserState } from '../../types'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { BaseStore } from './BaseStore'

export class UserStore extends BaseStore<UserState> {
  // 响应式状态
  private _userInfo: Ref<UserInfo | null>
  private _vipInfo: Ref<VipInfo | null>
  private _pointsInfo: Ref<PointsInfo | null>
  private _tasks: Ref<Task[]>

  // 计算属性
  public readonly isVip: ComputedRef<boolean>
  public readonly isVipExpired: ComputedRef<boolean>
  public readonly vipRemainingDays: ComputedRef<number>
  public readonly availablePoints: ComputedRef<number>

  constructor(storage: IStorage) {
    super(storage, STORAGE_KEYS.USER_INFO)

    // 初始化响应式状态
    this._userInfo = ref<UserInfo | null>(null)
    this._vipInfo = ref<VipInfo | null>(null)
    this._pointsInfo = ref<PointsInfo | null>(null)
    this._tasks = ref<Task[]>([])

    // 初始化计算属性
    // VipInfo 字段: status (0-未开通, 1-已开通, 2-已过期), expiry, expiry_day, permanent, points, soft_edition
    this.isVip = computed(() => {
      if (!this._vipInfo.value) return false
      // status: 1 表示已开通且未过期
      return this._vipInfo.value.status === 1
    })

    this.isVipExpired = computed(() => {
      if (!this._vipInfo.value) return false
      // status: 2 表示已过期
      return this._vipInfo.value.status === 2
    })

    this.vipRemainingDays = computed(() => {
      if (!this._vipInfo.value) return 0
      // 使用 expiry_day 字段
      return this._vipInfo.value.expiry_day ?? 0
    })

    this.availablePoints = computed(() => {
      if (!this._pointsInfo.value) return 0
      return this._pointsInfo.value.available ?? 0
    })

    // 从存储中恢复状态
    this.hydrate()
  }

  /**
   * 获取用户信息
   */
  get userInfo(): UserInfo | null {
    return this._userInfo.value
  }

  /**
   * 设置用户信息
   */
  set userInfo(value: UserInfo | null) {
    this._userInfo.value = value
    this.persist()
  }

  /**
   * 获取 VIP 信息
   */
  get vipInfo(): VipInfo | null {
    return this._vipInfo.value
  }

  /**
   * 设置 VIP 信息
   */
  set vipInfo(value: VipInfo | null) {
    this._vipInfo.value = value
    this.persist()
  }

  /**
   * 获取积分信息
   */
  get pointsInfo(): PointsInfo | null {
    return this._pointsInfo.value
  }

  /**
   * 设置积分信息
   */
  set pointsInfo(value: PointsInfo | null) {
    this._pointsInfo.value = value
    this.persist()
  }

  /**
   * 获取任务列表
   */
  get tasks(): Task[] {
    return this._tasks.value
  }

  /**
   * 设置任务列表
   */
  set tasks(value: Task[]) {
    this._tasks.value = value
    // 任务列表不需要持久化
  }

  /**
   * 更新用户信息
   */
  updateUserInfo(userInfo: Partial<UserInfo>): void {
    if (this._userInfo.value) {
      this._userInfo.value = { ...this._userInfo.value, ...userInfo }
      this.persist()
    }
  }

  /**
   * 更新积分
   */
  updatePoints(points: number): void {
    if (this._pointsInfo.value) {
      this._pointsInfo.value.available = points
      this._pointsInfo.value.total = points
      this.persist()
    }
  }

  /**
   * 清除用户信息
   */
  clearUserInfo(): void {
    this._userInfo.value = null
    this._vipInfo.value = null
    this._pointsInfo.value = null
    this._tasks.value = []
    this.clear()
  }

  /**
   * 序列化状态
   */
  serialize(): string {
    const state = {
      userInfo: this._userInfo.value,
      vipInfo: this._vipInfo.value,
      pointsInfo: this._pointsInfo.value
    }
    return JSON.stringify(state)
  }

  /**
   * 反序列化状态
   */
  deserialize(data: string): void {
    try {
      const state = JSON.parse(data)
      this._userInfo.value = state.userInfo || null
      this._vipInfo.value = state.vipInfo || null
      this._pointsInfo.value = state.pointsInfo || null
    } catch (error) {
      console.error('[UserStore] Failed to deserialize state', error)
    }
  }

  /**
   * 获取当前状态
   */
  getState(): UserState {
    return {
      userInfo: this._userInfo.value,
      vipInfo: this._vipInfo.value,
      pointsInfo: this._pointsInfo.value,
      tasks: this._tasks.value
    }
  }
}
